import { DialogTrigger, Pressable } from 'react-aria-components';
import { Paperclip } from 'lucide-react';
import { Popover } from '~/shared/components/Popover';
import { useGoalsAndInterventionsWizard } from '../GoalsAndInterventionsWizardContext';
import { InterventionCard } from '../../interventions/InterventionCard';
import type { WizardState } from '../reducer/wizardReducer';
import type { GoalViewModel, InterventionViewModel } from '@generated';

function getExistingGoalInterventions(wizardState: WizardState, existingInterventions: InterventionViewModel[]) {
	if (!wizardState.selectedGoal || wizardState.selectedGoal.type !== 'existing') {
		return [];
	}
	const selectedGoal = wizardState.selectedGoal as { type: 'existing'; existingGoal: GoalViewModel };
	return existingInterventions.filter(x => selectedGoal.existingGoal.interventionIds.includes(x.id));
}

export function SelectedInterventionList() {
	const { wizardState, dispatch, existingGoals, existingInterventions } = useGoalsAndInterventionsWizard();

	const existingGoalInterventions = getExistingGoalInterventions(wizardState, existingInterventions);
	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<span>Selected Interventions </span>
				{existingGoalInterventions.length > 0 && (
					<div className="flex items-center gap-2">
						<span className="text-sm text-gray-500">
							{existingGoalInterventions.length} existing intervention{existingGoalInterventions.length > 1 ? 's' : ''}
						</span>
						<DialogTrigger>
							<Pressable>
								<Paperclip role="button" className="size-5 cursor-pointer text-gray-500" />
							</Pressable>

							<Popover showArrow className="max-w-xs p-4">
								<span>{existingGoalInterventions.map(x => x.title).join(', ')}</span>
							</Popover>
						</DialogTrigger>
					</div>
				)}
			</div>

			{wizardState.selectedInterventions.length === 0 ? (
				<div className="text-center text-sm text-gray-500">No interventions selected</div>
			) : (
				wizardState.selectedInterventions
					.toSorted((a, b) => {
						const aTitle =
							a.type === 'existing'
								? a.existingIntervention.title
								: a.type === 'predefined'
								? a.interventionDefinition.title
								: 'Custom';
						const bTitle =
							b.type === 'existing'
								? b.existingIntervention.title
								: b.type === 'predefined'
								? b.interventionDefinition.title
								: 'Custom';
						return aTitle.localeCompare(bTitle);
					})
					.map(x => {
						if (x.type === 'existing') {
							const attachedGoals = existingGoals.filter(g => g.interventionIds.includes(x.existingIntervention.id));
							return (
								<InterventionCard
									key={x.id}
									interventionDefinition={x.existingIntervention.interventionDefinition!}
									selection={{
										selected: true,
										onSelect: () => dispatch({ type: 'INTERVENTION_UNSELECTED', interventionId: x.id })
									}}
									iconSlot={
										<DialogTrigger>
											<Pressable>
												<Paperclip role="button" className="size-5 cursor-pointer text-gray-500" />
											</Pressable>

											<Popover showArrow className="max-w-xs p-4">
												<span>
													Attached Goals:&nbsp;
													{attachedGoals.map(x => x.title).join(', ')}
												</span>
											</Popover>
										</DialogTrigger>
									}
								/>
							);
						} else if (x.type === 'predefined') {
							return (
								<InterventionCard
									key={x.id}
									interventionDefinition={x.interventionDefinition!}
									selection={{
										selected: true,
										onSelect: () => dispatch({ type: 'INTERVENTION_UNSELECTED', interventionId: x.id })
									}}
								/>
							);
						}

						return (
							<InterventionCard
								key={x.id}
								interventionDefinition={{
									title: 'Custom',
									definitionText: 'Custom intervention definition',
									documentationWorkflows: [],
									restrictedToClinicalTypes: []
								}}
								selection={{
									selected: true,
									onSelect: () => dispatch({ type: 'INTERVENTION_UNSELECTED', interventionId: x.id })
								}}
							/>
						);
					})
			)}
		</div>
	);
}
